import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Button, Spinner } from 'reactstrap';
import { Link, useParams, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import axios from 'axios';
import { toCurrency, escapeHtml, isFailedResponse } from '@/shared/helpers';
import { useDispatch, flash, flashSuccess, flashError } from '@/shared/components/flash';
import ConfirmPopup from '@/shared/components/modal/confirmModal/ConfirmPopup';
import MarkSafeModal from './components/MarkSafeModal';
import DeclineHoldModal from '@/shared/components/DeclineHoldModal';
import { invalidateRedemptionRiskEvents } from './components/RedemptionRiskEventsTable';
import { SectionCard, KeyValueGrid, JsonBlock } from './components/DetailSections';
import { formatAccountAge, formatDateTime, userName, classificationBadgeClass } from './components/helpers';
import FullPageLoader from '@/shared/components/FullPageLoader';

const RedemptionRiskEventView = ({ organization }) => {
    const { id } = useParams();
    const flashDispatcher = useDispatch();
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isApproveConfirmOpen, setApproveConfirmOpen] = useState(false);
    const [replayConfirmMessage, setReplayConfirmMessage] = useState(null);
    const [isDeclineOpen, setDeclineOpen] = useState(false);
    const [isMarkSafeOpen, setMarkSafeOpen] = useState(false);
    const [isApproving, setApproving] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);

    const currentIdRef = useRef(id);

    // Following an "event #X" link reuses this component; drop the previous
    // event's details so its Approve/Decline can't act on the old hold.
    useEffect(() => {
        currentIdRef.current = id;
        setDetail(null);
        setLoadFailed(false);
    }, [id]);

    const fetchDetail = useCallback(() => {
        if (!id || !organization?.id) return;
        setLoading(true);
        axios.get(`/organization/${organization.id}/redemption-risk-event/${id}`)
            .then(res => {
                if (currentIdRef.current !== id) return;
                if (isFailedResponse(res)) {
                    setLoadFailed(true);
                    setDetail(null);
                    return;
                }
                setLoadFailed(false);
                setDetail(res.data);
            })
            .finally(() => {
                if (currentIdRef.current === id) setLoading(false);
            });
    }, [id, organization?.id]);

    useEffect(() => { fetchDetail(); }, [fetchDetail]);

    const refreshAfterAction = () => {
        fetchDetail();
        invalidateRedemptionRiskEvents();
    };

    if (loading && !detail) return <p>Loading...</p>;
    if (loadFailed || !detail) return <p>This redemption risk event could not be loaded. It may not exist, or you may not have access to it.</p>;

    const {
        score, component_scores: componentScores, extreme_velocity: extremeVelocity,
        velocity_inputs: velocityInputs, award_balance_context: awardBalanceContext,
        personal_velocity: personalVelocity, redemption_history: redemptionHistory,
        baseline_snapshot: baselineSnapshot, config_snapshot: configSnapshot,
        hold, review_decision: reviewDecision, reviewer, reviewed_at: reviewedAt,
        account, history, safe_review_history: safeReviewHistory,
    } = detail;

    const programId = score?.program_id || hold?.program_id;
    const user = account?.user;
    // Mark Safe clears the account whichever event put it under review.
    const accountUnderReview = !!account?.redemption_review_status;

    const confirmApprove = (confirmReplay = false) => {
        setApproveConfirmOpen(false);
        setReplayConfirmMessage(null);
        setApproving(true);
        axios.put(`/organization/${organization.id}/program/${programId}/redemption-hold/${hold.id}/approve`, confirmReplay ? { confirm_replay: true } : undefined)
            .then((response) => {
                refreshAfterAction(); // re-sync either way: the hold reverts to PENDING server-side on failure
                // The participant bought from the held merchant since the hold; replaying may duplicate it.
                if (response?.data?.confirmation_required) {
                    setReplayConfirmMessage(response.data.message);
                    return;
                }
                if (isFailedResponse(response)) {
                    if (!response?.data) flash('warning', flashDispatcher, 'The approval did not complete a response, so its outcome is unknown. The hold has been refreshed - check its status before trying again.');
                    return;
                }
                if (response.data.warnings?.length) {
                    flash('warning', flashDispatcher, response.data.warnings.join(' '));
                    return;
                }
                flashSuccess(flashDispatcher, 'Redemption approved and processed.');
            })
            .catch(error => flashError(flashDispatcher, error.response?.data))
            .finally(() => setApproving(false));
    };

    return (
        <Container className="dashboard">
            <FullPageLoader show={isApproving} text="Approving redemption..." />
            <Row>
                <Col md={12}>
                    <h3 className="page-title">Redemption Risk Event #{id}</h3>
                    <h3 className="page-subhead subhead">
                        <Link to="/">Home</Link> / <Link to="/approvals/redemptions">Redemptions</Link> / #{id}
                    </h3>
                </Col>
            </Row>

            {/*
              align-items-start: this theme's global `.card { height: 100% }`
              (meant for side-by-side dashboard widgets) combines with
              Bootstrap's default `.row { align-items: stretch }` to create a
              runaway height feedback loop when multiple cards are stacked
              vertically in one column, as they are here - each stacked card
              inflates to match its stretched column's height, which in turn
              is driven by the tallest column. Without this, the page renders
              over 15,000px tall.
            */}
            <Row className="align-items-start">
                <Col md={8}>
                    <SectionCard title="Overview">
                        <KeyValueGrid items={[
                            ['User', userName(user)],
                            ['Email', user?.email],
                            ['Program', score?.program?.name || (score?.program_id ? `#${score.program_id}` : '—')],
                            ['Merchant', score?.merchant?.name || (score?.merchant_id ? `#${score.merchant_id}` : '—')],
                            ['Amount', toCurrency(score?.cart_value_total ?? score?.redemption_value)],
                            ['Codes in Checkout', score?.cart_code_count ?? 1],
                            ['Checkout Type', hold?.checkout_type],
                            ['Date/Time', formatDateTime(score?.redemption_datetime)],
                            ['Classification', <span className={classificationBadgeClass(score?.classification)}>{score?.classification}</span>],
                            ['Final Score', score?.final_score],
                            ['Initial Score', score?.initial_score],
                            ['Risk Reduction', score?.risk_reduction],
                            ['Reduction Reason', score?.reduction_reason],
                            ['Account Age', formatAccountAge(score?.account_age_hours)],
                        ]} />
                    </SectionCard>

                    <SectionCard title="Component Risk Factors" className="mt-3">
                        <KeyValueGrid items={[
                            ['Account Age Score', componentScores?.account_age_score],
                            ['Population Velocity Score', componentScores?.population_velocity_score],
                            ['Progressive Attempt Score', componentScores?.progressive_attempt_score],
                            ['Redemption Velocity Score', componentScores?.redemption_velocity_score],
                            ['Dollar Velocity Score', componentScores?.dollar_velocity_score],
                            ['Historical Deviation Score', componentScores?.historical_deviation_score],
                            ['Merchant Behavior Score', componentScores?.merchant_behavior_score],
                            ['New Account Velocity Score', componentScores?.new_account_velocity_score],
                            ['Personal Velocity Score', componentScores?.personal_velocity_score],
                        ]} />
                    </SectionCard>

                    <SectionCard title="Personal Velocity" className="mt-3">
                        {personalVelocity?.baseline_snapshot && Object.keys(personalVelocity.baseline_snapshot).length > 0 ? (
                            <>
                                <KeyValueGrid items={[
                                    ['Score', personalVelocity?.score],
                                    ['24h Count vs Baseline', personalVelocity?.count_ratio !== null && typeof personalVelocity?.count_ratio !== 'undefined' ? `${personalVelocity.count_ratio}x` : null],
                                    ['3-Day Dollars vs Baseline', personalVelocity?.dollar_ratio !== null && typeof personalVelocity?.dollar_ratio !== 'undefined' ? `${personalVelocity.dollar_ratio}x` : null],
                                ]} />
                                <JsonBlock label="Personal Velocity Baseline" value={personalVelocity.baseline_snapshot} />
                            </>
                        ) : <p className="text-muted">Personal velocity was not enabled when this redemption was scored.</p>}
                    </SectionCard>

                    <SectionCard title="Extreme Velocity" className="mt-3">
                        <KeyValueGrid items={[
                            ['Triggered', extremeVelocity?.triggered ? 'Yes' : 'No'],
                            ['Reason', extremeVelocity?.reason],
                            ['Same-Second Count', extremeVelocity?.same_second_count],
                            ['10s Window Count', extremeVelocity?.window_10s_count],
                            ['60s Window Count', extremeVelocity?.window_60s_count],
                            ['60s Window Value', extremeVelocity?.window_60s_value !== null && typeof extremeVelocity?.window_60s_value !== 'undefined' ? toCurrency(extremeVelocity.window_60s_value) : null],
                            ['Merchant Changes (60s)', extremeVelocity?.merchant_changes_60s],
                        ]} />
                    </SectionCard>

                    <SectionCard title="Velocity Inputs" className="mt-3">
                        <KeyValueGrid items={[
                            ['Redemptions (24h)', velocityInputs?.redemption_count_24h],
                            ['Redemptions (3d)', velocityInputs?.redemption_count_3d],
                            ['Redemption Value (3d)', velocityInputs?.redemption_value_3d !== null && typeof velocityInputs?.redemption_value_3d !== 'undefined' ? toCurrency(velocityInputs.redemption_value_3d) : null],
                            ['Merchant Concentration Ratio', velocityInputs?.merchant_concentration_ratio],
                        ]} />
                    </SectionCard>

                    <SectionCard title="Award / Balance Context & Snapshots" className="mt-3">
                        <JsonBlock label="Award/Balance Context" value={awardBalanceContext} />
                        <JsonBlock label="Baseline Snapshot" value={baselineSnapshot} />
                        <JsonBlock label="Config Snapshot" value={configSnapshot} />
                    </SectionCard>

                    <SectionCard title="Redemptions" className="mt-3">
                        {(!redemptionHistory || redemptionHistory.length === 0) && <p className="text-muted">No redemptions up to this event.</p>}
                        {redemptionHistory && redemptionHistory.length > 0 && (
                            <table className="table table-sm table--bordered">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Merchant</th>
                                        <th>Program</th>
                                        <th>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {redemptionHistory.map(r => (
                                        <tr key={r.id}>
                                            <td>{formatDateTime(r.redemption_datetime)}</td>
                                            <td>{r.merchant_name || (r.merchant_id ? `#${r.merchant_id}` : '—')}</td>
                                            <td>{r.program_name || (r.program_id ? `#${r.program_id}` : '—')}</td>
                                            <td>{toCurrency(r.redemption_value)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </SectionCard>

                    <SectionCard title="Risk Event History" className="mt-3">
                        {(!history || history.length === 0) && <p className="text-muted">No other risk events for this user.</p>}
                        {history && history.length > 0 && (
                            <table className="table table-sm table--bordered">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Amount</th>
                                        <th>Classification</th>
                                        <th>Score</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {history.map(h => (
                                        <tr key={h.id}>
                                            <td>{formatDateTime(h.redemption_datetime)}</td>
                                            <td>{toCurrency(h.cart_value_total ?? h.redemption_value)}</td>
                                            <td><span className={classificationBadgeClass(h.classification)}>{h.classification}</span></td>
                                            <td>{h.final_score}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </SectionCard>

                    <SectionCard title="Mark Redeemer Safe History" className="mt-3">
                        {(!safeReviewHistory || safeReviewHistory.length === 0) && <p className="text-muted">No prior clearances.</p>}
                        {safeReviewHistory && safeReviewHistory.length > 0 && (
                            <table className="table table-sm table--bordered">
                                <thead>
                                    <tr>
                                        <th>Cleared At</th>
                                        <th>Reviewer</th>
                                        <th>Previous Status</th>
                                        <th>Note</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {safeReviewHistory.map(c => (
                                        <tr key={c.id}>
                                            <td>{formatDateTime(c.cleared_at)}</td>
                                            <td>{userName(c.reviewer)}</td>
                                            <td>{c.previous_status}</td>
                                            <td>{c.review_note}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </SectionCard>
                </Col>

                <Col md={4}>
                    <SectionCard title="Account Review State">
                        <KeyValueGrid items={[
                            ['Currently Under Review', accountUnderReview
                                ? (account?.is_under_review
                                    ? 'Yes, because of this event'
                                    : <>Yes, because of <Link to={`/approvals/redemptions/${account?.redemption_review_score_id}`}>event #{account?.redemption_review_score_id}</Link></>)
                                : 'No'],
                            ['Review Status', account?.redemption_review_status],
                            ['Under Review Since', formatDateTime(account?.redemption_review_since)],
                        ]} />
                        <Button
                            color="primary"
                            size="sm"
                            className="w-100 mt-2"
                            disabled={!accountUnderReview}
                            onClick={() => setMarkSafeOpen(true)}
                        >
                            Mark Redeemer Safe
                        </Button>
                        {!accountUnderReview && (
                            <small className="text-muted d-block mt-1">This account is not currently under review.</small>
                        )}
                    </SectionCard>

                    <SectionCard title="Pending Redemption" className="mt-3">
                        {!hold && <p className="text-muted">No hold was created for this event (Watch, or Normal).</p>}
                        {hold && (
                            <>
                                <KeyValueGrid items={[
                                    ['Hold Status', hold.status],
                                    ['Created', formatDateTime(hold.created_at)],
                                    ['Reviewed At', formatDateTime(hold.reviewed_at)],
                                    ['Review Notes', hold.review_notes],
                                    ['Refused Retries While Under Review', hold.retry_count
                                        ? `${hold.retry_count} (last ${formatDateTime(hold.last_retry_at)})`
                                        : '0'],
                                ]} />
                                {hold.status === 'PROCESSING' && (
                                    <p className="text-muted mt-2">
                                        Approval in progress. If it does not finish, approving or declining again after a few minutes
                                        settles it from whether the purchase went through.
                                    </p>
                                )}
                                {['PENDING', 'PROCESSING'].includes(hold.status) && (
                                    <div className="d-flex gap-2 mt-2">
                                        <Button color="success" size="sm" disabled={isApproving} onClick={() => setApproveConfirmOpen(true)}>
                                            {isApproving ? <>Approving <Spinner size="sm" className="ms-1 align-middle" /></> : 'Approve'}
                                        </Button>
                                        <Button color="secondary" size="sm" disabled={isApproving} onClick={() => setDeclineOpen(true)}>Decline</Button>
                                    </div>
                                )}
                                {['APPROVED', 'DECLINED'].includes(hold.status) && (
                                    <p className="text-muted mt-2">This held redemption has already been {hold.status.toLowerCase()}.</p>
                                )}
                            </>
                        )}
                    </SectionCard>

                    <SectionCard title="This Event's Review Decision" className="mt-3">
                        <KeyValueGrid items={[
                            ['Decision', reviewDecision],
                            ['Reviewer', userName(reviewer)],
                            ['Reviewed At', formatDateTime(reviewedAt)],
                        ]} />
                    </SectionCard>
                </Col>
            </Row>

            {user && (
                <MarkSafeModal
                    user={user}
                    hold={hold}
                    pendingHolds={account?.pending_holds}
                    redemptionValue={score?.cart_value_total ?? score?.redemption_value}
                    isOpen={isMarkSafeOpen}
                    toggle={() => setMarkSafeOpen(prev => !prev)}
                    onSuccess={refreshAfterAction}
                />
            )}

            {hold && (
                <>
                    <ConfirmPopup
                        isOpen={isApproveConfirmOpen}
                        toggle={() => setApproveConfirmOpen(false)}
                        action={() => confirmApprove()}
                        message={`Approve and process the held redemption for ${escapeHtml(userName(user))}? <br/>This will complete the original purchase.`}
                    />
                    <ConfirmPopup
                        isOpen={!!replayConfirmMessage}
                        toggle={() => setReplayConfirmMessage(null)}
                        action={() => confirmApprove(true)}
                        message={`${escapeHtml(replayConfirmMessage || '')} <br/>Process the original cart anyway?`}
                    />
                    <DeclineHoldModal
                        programId={programId}
                        holdId={hold.id}
                        description={<>Held redemption of {(score?.cart_value_total ?? score?.redemption_value) ? toCurrency(score?.cart_value_total ?? score?.redemption_value) : ''} — this will not process the original purchase.</>}
                        isOpen={isDeclineOpen}
                        toggle={() => setDeclineOpen(prev => !prev)}
                        onSuccess={refreshAfterAction}
                    />
                </>
            )}
        </Container>
    );
};

export default withRouter(connect((state) => ({
    organization: state.organization,
}))(RedemptionRiskEventView));
